import {
  createEmptyCard,
  fsrs,
  generatorParameters,
  Rating,
  State,
  type Card,
  type Grade,
} from 'ts-fsrs';

/**
 * What the app knows about a learner's grip on one skill.
 *
 * Two numbers, doing different jobs. `mastery` drives difficulty: how hard the
 * next question about this skill should be. The FSRS card drives scheduling:
 * when the skill should come back. A skill can be well understood and still due
 * for review, and the app needs both facts.
 */

export interface SkillState {
  skillId: string;
  /** 0..1 estimate of how well the skill is held. */
  mastery: number;
  card: Card;
  /** Consecutive correct answers. */
  streak: number;
  lastDifficulty: number;
  seen: number;
  /** True once the concept card has been shown. */
  introduced: boolean;
}

const scheduler = fsrs(
  generatorParameters({
    enable_fuzz: true,
    request_retention: 0.9,
    maximum_interval: 180,
  }),
);

export function newSkillState(skillId: string, now = new Date()): SkillState {
  return {
    skillId,
    mastery: 0,
    card: createEmptyCard(now),
    streak: 0,
    lastDifficulty: 1,
    seen: 0,
    introduced: false,
  };
}

/** The difficulty a skill at this mastery should be asked at. */
export function targetDifficulty(mastery: number): number {
  return Math.max(1, Math.min(10, Math.round(1 + mastery * 9)));
}

export interface AttemptOutcome {
  /** 0..1; anything below 0.5 counts as a miss. */
  score: number;
  difficulty: number;
  responseMs: number;
  /** A hinted answer earns less credit. */
  hinted?: boolean;
}

/**
 * Updates mastery from one attempt.
 *
 * The move is proportional to how surprising the result was: getting a question
 * right at a difficulty well above your level moves mastery more than getting an
 * easy one right. Misses move it further than hits, because forgetting is
 * better evidence than a lucky guess.
 */
export function updateMastery(state: SkillState, outcome: AttemptOutcome): SkillState {
  const { score, difficulty } = outcome;

  /*
   * How likely this was to go right, from how far the question sat from the
   * learner's level (doc 08).
   *
   * Capped below 1, which doc 08's formula does not do, because at exactly the
   * learner's level it gives 1.0 — and then a correct answer has a delta of
   * exactly zero and moves nothing. For a skill whose only generator covers
   * band 1, the target is 1 and the question is always 1, so mastery sat at
   * zero however many times it was answered correctly: `pitch.concept` and six
   * others could never be learned at all. The perfect-student simulation found
   * it. Nothing is ever certain, so nothing is ever expected at 1.
   */
  const EXPECTED_CEILING = 0.95;
  const expected = Math.min(
    EXPECTED_CEILING,
    0.3 + 0.7 * (1 - Math.abs(difficulty - targetDifficulty(state.mastery)) / 9),
  );
  const delta = score - expected;
  const weight = 0.12 + 0.08 * (difficulty / 10);
  const missPenalty = score >= 0.5 ? 1 : 1.5;
  const hintFactor = outcome.hinted === true && delta > 0 ? 0.5 : 1;

  const mastery = clamp01(state.mastery + weight * delta * missPenalty * hintFactor);

  return {
    ...state,
    mastery,
    streak: score >= 0.5 ? state.streak + 1 : 0,
    lastDifficulty: difficulty,
    seen: state.seen + 1,
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/* --------------------------------------------------------------- grading */

/** Median response time per template, used to tell fast answers from slow ones. */
export const DEFAULT_MEDIAN_RESPONSE_MS = 8000;

/**
 * Turns an attempt into an FSRS rating.
 *
 * Wrong is Again. Slow-but-right is Hard, because hesitation predicts
 * forgetting. Fast and right on a stretch question is Easy.
 */
export function ratingFor(
  outcome: AttemptOutcome,
  state: SkillState,
  medianMs = DEFAULT_MEDIAN_RESPONSE_MS,
): Grade {
  if (outcome.score < 0.5) return Rating.Again;
  if (outcome.responseMs > medianMs * 2) return Rating.Hard;

  const fast = outcome.responseMs < medianMs * 0.6;
  const confident = state.streak >= 3;
  const stretching = outcome.difficulty >= targetDifficulty(state.mastery);

  return fast && confident && stretching ? Rating.Easy : Rating.Good;
}

/** Applies a rating to the FSRS card, producing the next due date. */
export function schedule(state: SkillState, rating: Grade, now = new Date()): SkillState {
  const result = scheduler.repeat(state.card, now);
  const next = result[rating];
  return { ...state, card: next.card };
}

/**
 * Applies a whole session's worth of attempts on one skill.
 *
 * Mastery moves per attempt, but the card is scheduled once, from the worst
 * rating of the session. Otherwise a skill asked five times in a lesson would be
 * rescheduled five times and its interval would drift.
 */
export function applySession(
  state: SkillState,
  outcomes: readonly AttemptOutcome[],
  options: { now?: Date; medianMs?: number } = {},
): SkillState {
  if (outcomes.length === 0) return state;
  const now = options.now ?? new Date();

  let next = state;
  for (const outcome of outcomes) next = updateMastery(next, outcome);

  const worst = outcomes
    .map((o) => ratingFor(o, state, options.medianMs))
    .reduce<Grade>((lowest, r) => (r < lowest ? r : lowest), Rating.Easy);

  return schedule(next, worst, now);
}

/* --------------------------------------------------------------- queries */

export function isDue(state: SkillState, now = new Date()): boolean {
  if (state.card.state === State.New) return false;
  return state.card.due.getTime() <= now.getTime();
}

export function daysOverdue(state: SkillState, now = new Date()): number {
  if (state.card.state === State.New) return 0;
  return (now.getTime() - state.card.due.getTime()) / 86_400_000;
}

/** Mastered: well held and scheduled far enough out to be considered learned. */
export function isMastered(state: SkillState): boolean {
  return state.mastery >= 0.9 && state.card.stability >= 30;
}

/** Weak: repeatedly attempted and still not holding. */
export function isWeak(state: SkillState): boolean {
  return state.seen >= 6 && state.mastery < 0.5;
}

/** Skills due now, most overdue first. */
export function dueSkills(states: readonly SkillState[], now = new Date()): SkillState[] {
  return states
    .filter((s) => isDue(s, now))
    .sort((a, b) => daysOverdue(b, now) - daysOverdue(a, now));
}

/** Weakest skills first, for the practice hub. */
export function weakestSkills(states: readonly SkillState[], limit = 6): SkillState[] {
  return states
    .filter(isWeak)
    .sort((a, b) => a.mastery - b.mastery)
    .slice(0, limit);
}

export { Rating, State };
export type { Card, Grade };
